import {
  patchState,
  signalStore,
  withComputed,
  withMethods,
  withState,
} from '@ngrx/signals';
import { computed, inject } from '@angular/core';
import {
  DEFAULT_NOTIFICATION_PREFERENCES,
  NotificationPreferences,
  normalizeReminderTimes,
  REMINDER_TIME_OPTIONS,
  REMINDER_TIMES_MAX,
} from '@equip-track/shared';
import { firstValueFrom } from 'rxjs';
import { ApiService } from '../services/api.service';
import { LanguageService } from '../services/language.service';
import { NotificationService } from '../services/notification.service';
import { PushNotificationService } from '../services/push-notification.service';

interface NotificationSettingsState {
  preferences: NotificationPreferences;
  vapidPublicKey: string | null;
  isLoading: boolean;
  isSaving: boolean;
  loaded: boolean;
}

const initialState: NotificationSettingsState = {
  preferences: { ...DEFAULT_NOTIFICATION_PREFERENCES },
  vapidPublicKey: null,
  isLoading: false,
  isSaving: false,
  loaded: false,
};

export const NotificationSettingsStore = signalStore(
  { providedIn: 'root' },
  withState(initialState),
  withComputed((state) => {
    const pushNotificationService = inject(PushNotificationService);
    return {
      browserChannelEnabled: computed(
        () => state.preferences().channels.browser
      ),
      remindersEnabled: computed(() => state.preferences().enabled),
      reminderTimes: computed(() => state.preferences().reminderTimes),
      pushSupported: computed(() => pushNotificationService.isSupported()),
    };
  }),
  withMethods((store) => {
    const apiService = inject(ApiService);
    const languageService = inject(LanguageService);
    const notificationService = inject(NotificationService);
    const pushNotificationService = inject(PushNotificationService);

    return {
      async load(): Promise<void> {
        if (store.isLoading()) {
          return;
        }
        patchState(store, { isLoading: true });
        try {
          const response = await firstValueFrom(
            apiService.endpoints.getNotificationSettings.execute(undefined)
          );
          patchState(store, {
            preferences: response.preferences,
            vapidPublicKey: response.vapidPublicKey,
            loaded: true,
            isLoading: false,
          });
        } catch (error) {
          patchState(store, { isLoading: false });
          notificationService.handleApiError(error);
        }
      },

      async savePreferences(
        partial: Pick<
          NotificationPreferences,
          'enabled' | 'reminderTimes' | 'channels' | 'language'
        >
      ): Promise<boolean> {
        patchState(store, { isSaving: true });
        try {
          const payload = {
            ...partial,
            language: languageService.language(),
          };

          const response = await firstValueFrom(
            apiService.endpoints.setNotificationSettings.execute({
              preferences: payload,
            })
          );

          patchState(store, {
            preferences: response.preferences,
            isSaving: false,
          });

          const wantsBrowserPush =
            response.preferences.enabled &&
            response.preferences.channels.browser;
          const vapidPublicKey = store.vapidPublicKey();

          const subscribed = await pushNotificationService.syncIfEnabled(
            response.preferences.enabled,
            response.preferences.channels.browser,
            vapidPublicKey
          );

          if (wantsBrowserPush && !subscribed) {
            // Distinguish a server misconfiguration (no VAPID key) from a
            // genuine browser permission denial so the message is accurate.
            notificationService.showInfo(
              vapidPublicKey
                ? 'notifications.permission-denied'
                : 'notifications.unavailable'
            );
            patchState(store, {
              preferences: {
                ...response.preferences,
                enabled: false,
                channels: { ...response.preferences.channels, browser: false },
              },
            });
            await firstValueFrom(
              apiService.endpoints.setNotificationSettings.execute({
                preferences: {
                  enabled: false,
                  reminderTimes: response.preferences.reminderTimes,
                  channels: { browser: false },
                  language: languageService.language(),
                },
              })
            );
            return false;
          }

          return true;
        } catch (error) {
          patchState(store, { isSaving: false });
          notificationService.handleApiError(error);
          return false;
        }
      },

      async syncLanguageIfEnabled(): Promise<void> {
        if (!store.loaded() || !store.preferences().enabled) {
          return;
        }
        await this.savePreferences({
          enabled: store.preferences().enabled,
          reminderTimes: store.preferences().reminderTimes,
          channels: store.preferences().channels,
          language: languageService.language(),
        });
      },

      /** Persist a new reminder-times list (normalized + capped) with autosave. */
      async persistReminderTimes(times: string[]): Promise<void> {
        const normalized = normalizeReminderTimes(times).slice(
          0,
          REMINDER_TIMES_MAX
        );
        if (normalized.length === 0) {
          return;
        }
        await this.savePreferences({
          enabled: store.remindersEnabled(),
          reminderTimes: normalized,
          channels: store.preferences().channels,
          language: languageService.language(),
        });
      },

      async addReminderTime(): Promise<void> {
        const current = store.reminderTimes();
        if (current.length >= REMINDER_TIMES_MAX) {
          return;
        }
        const next = REMINDER_TIME_OPTIONS.find((t) => !current.includes(t));
        if (!next) {
          return;
        }
        await this.persistReminderTimes([...current, next]);
      },

      async removeReminderTime(index: number): Promise<void> {
        const current = store.reminderTimes();
        if (current.length <= 1 || index < 0 || index >= current.length) {
          return;
        }
        await this.persistReminderTimes(current.filter((_, i) => i !== index));
      },

      async updateReminderTime(index: number, value: string): Promise<void> {
        const current = store.reminderTimes();
        if (
          index < 0 ||
          index >= current.length ||
          current[index] === value ||
          current.includes(value)
        ) {
          return;
        }
        await this.persistReminderTimes(
          current.map((t, i) => (i === index ? value : t))
        );
      },

      async restorePushSubscriptionOnStartup(): Promise<void> {
        if (!store.loaded()) {
          await this.load();
        }
        const prefs = store.preferences();
        if (!prefs.enabled || !prefs.channels.browser) {
          return;
        }
        await pushNotificationService.syncIfEnabled(
          prefs.enabled,
          prefs.channels.browser,
          store.vapidPublicKey()
        );
      },
    };
  })
);
